import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useDispatch } from "react-redux";
import { AppThunkDispatch } from "@/store";
import actionsHandler from "@/store/actions/actionHandler";
import { useLiveChatState } from "../Provider/LiveChatStateProvider";
import ChatMessages from "../components/ChatMessages";
import {
  Document,
  DocumentMaster,
  DocumentMasterResponse,
} from "../types/document-types";
import closeIcon from "@styles/all-images/icons/close.svg";
import { toast } from "react-toastify";
import LiveChatFinancialDocumentSkeleton from "../components/skeletons/LiveChatFinancialDocumentSkeleton";
import ElipsedText from "@/components/common/elipsis/ElipsedText";

interface LiveChatFinancialDocumentProps {
  type: "financial" | "legal";
}

const LiveChatFinancialDocument: React.FC<LiveChatFinancialDocumentProps> = ({
  type,
}) => {
  const dispatch = useDispatch<AppThunkDispatch>();
  const { userInfo, setDocumentChatRoomIds, unreadCounts, resetUnreadCount } =
    useLiveChatState();
  const [isLoading, setIsLoading] = useState(false);
  const [documentMaster, setDocumentMaster] =
    useState<DocumentMasterResponse | null>(null);
  const [activeTab, setActiveTab] = useState<string>("");
  const [selectedDocument, setSelectedDocument] = useState<number | null>(null);
  const [isThreadVisible, setIsThreadVisible] = useState(false);
  const [threadMessageId, setThreadMessageId] = useState<number | null>(null);
  const [documentChatRoomId, setDocumentChatRoomId] = useState<string | null>(
    null,
  );

  const tabs = useMemo(() => {
    if (!documentMaster) return [];
    const documents =
      type === "financial"
        ? documentMaster.data.financial
        : documentMaster.data.legal;
    return Object.entries(documents)
      .sort((a, b) => a[1].sequence - b[1].sequence)
      .map(([key, value]) => ({
        id: value.id,
        label: key,
        hasUnread: value.documents.some(
          (doc) => doc.uploaded_document?.status === 1, // Pending status
        ),
      }));
  }, [documentMaster, type]);

  const currentTabDocuments = useMemo(() => {
    if (!documentMaster || !activeTab) return [];
    const documents =
      type === "financial"
        ? documentMaster.data.financial
        : documentMaster.data.legal;
    return documents[activeTab]?.documents || [];
  }, [documentMaster, activeTab, type]);

  const fetchDocumentMaster = async () => {
    try {
      setIsLoading(true);
      const { authenticatedCall } = actionsHandler("viewDocumentMaster");
      const response = await dispatch(
        authenticatedCall({
          method: "get",
          customParams: [userInfo?.id.toString() + "/"],
        }),
      );

      if (response.ok) {
        setDocumentMaster(response.data);
      }
    } catch (error) {
      console.error("Error fetching document master:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (userInfo?.id) {
      fetchDocumentMaster();
    }
  }, [userInfo]);

  useEffect(() => {
    if (tabs.length > 0) {
      setIsThreadVisible(false);
      setActiveTab(tabs[0].label);
    }
  }, [tabs, type]);

  useEffect(() => {
    setIsThreadVisible(false);
    if (tabs.length > 0) {
      const document = currentTabDocuments[0];
      if (document) {
        handleDocumentClick(document);
      }
    }
  }, [tabs, activeTab, currentTabDocuments]);

  useEffect(() => {
    setIsThreadVisible(false);
  }, [selectedDocument]);

  useEffect(() => {
    if (!isThreadVisible) {
      setThreadMessageId(null);
    }
  }, [isThreadVisible]);

  useEffect(() => {
    if (documentChatRoomId) {
      setDocumentChatRoomIds((prev) => [
        ...prev,
        documentChatRoomId?.toString(),
      ]);
    }
  }, [documentChatRoomId]);

  useEffect(() => {
    if (documentMaster) {
      setDocumentListChatRoomIds(documentMaster.data?.financial);
      setDocumentListChatRoomIds(documentMaster.data?.legal);
    }
  }, [documentMaster]);

  const setDocumentListChatRoomIds = (
    documents: Record<string, DocumentMaster>,
  ) => {
    const documentListChatRoomIds = Object.keys(documents).map((key) => {
      return documents[key as keyof typeof documents].documents.map(
        (document) => document.uploaded_document?.room_id?.toString() || "",
      );
    });
    setDocumentChatRoomIds((prev) => {
      const newDocumentChatRoomIds = [
        ...prev,
        ...documentListChatRoomIds.flat(),
      ].filter((roomId) => roomId !== "");
      const uniqueDocumentChatRoomIds = [...new Set(newDocumentChatRoomIds)];
      return uniqueDocumentChatRoomIds;
    });
  };

  const handleSettingDocumentChatRoomId = async (document: Document) => {
    if (document.uploaded_document?.room_id) {
      setDocumentChatRoomId(document.uploaded_document.room_id.toString());
      resetUnreadCount(document.uploaded_document.room_id.toString());
    } else {
      try {
        const { authenticatedCall } = actionsHandler("createDocumentChatRoom");
        const response = await dispatch(
          authenticatedCall({
            method: "post",
            payload: {
              document_id: document.uploaded_document.id,
              recipient_id: userInfo?.id,
            },
          }),
        );
        if (response.ok) {
          setDocumentChatRoomId(response.data?.room_id?.toString() || null);
          resetUnreadCount(response.data?.room_id?.toString() || null);
        }
      } catch (error) {
        console.error("Error creating chat room:", error);
      }
    }
  };

  const handleDocumentClick = (document: Document) => {
    if (document.uploaded_document?.id) {
      setIsThreadVisible(false);
      setSelectedDocument(document.uploaded_document.id);
      handleSettingDocumentChatRoomId(document);
    } else {
      toast.warning("Uploaded information is not available yet");
    }
  };

  const getUnreadCount = useCallback(
    (roomId?: number) => {
      return roomId && unreadCounts[roomId] > 0 ? unreadCounts[roomId] : 0;
    },
    [unreadCounts],
  );

  if (isLoading) {
    return <LiveChatFinancialDocumentSkeleton />;
  }

  return (
    <div className="tw-w-full tw-bg-[#FFFFFF] tw-border tw-border-[#E9EAEB] tw-rounded-lg tw-overflow-hidden tw-pb-4">
      <div className="tw-px-8 tw-my-3 tw-overflow-hidden tw-h-[60px]">
        <nav className="tw-flex tw-w-full">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => {
                setActiveTab(tab.label);
                setIsThreadVisible(false);
              }}
              className={`
                tw-px-4 tw-py-2 tw-text-sm tw-font-semibold tw-border
                tw-transition-colors tw-duration-200 tw-flex tw-text-nowrap
                ${
                  activeTab === tab.label
                    ? "tw-bg-white tw-text-[#146C94] tw-font-semibold tw-border-b-2 tw-border-b-[#146C94]"
                    : "tw-bg-[#FFFFFF] tw-text-[#475569] tw-font-semibold"
                }
                ${tab.id !== tabs[0].id ? "tw-border-l-0" : ""}
              `}
            >
              {tab.label}
              {/* {tab.hasUnread && (
                <p className="tw-ml-2 tw-flex tw-w-4 tw-h-4 tw-bg-[#FFE0CF] tw-rounded-full tw-justify-center tw-items-center">
                  <span className="tw-w-[2px] tw-h-[2px] tw-p-[2px] tw-bg-[#B54708] tw-rounded-full"></span>
                </p>
              )} */}
            </button>
          ))}
        </nav>
      </div>

      <div className="tw-flex tw-gap-4 tw-px-8">
        <div className="tw-bg-[#FFFFFF] tw-border tw-border-[#E9EAEB] tw-rounded-lg tw-p-5 tw-w-[25%]">
          {currentTabDocuments.map((doc) => (
            <button
              key={doc.id}
              onClick={() => handleDocumentClick(doc)}
              className={`tw-mb-4 tw-p-4 ${
                selectedDocument === doc.uploaded_document?.id
                  ? "tw-bg-[#EEF2FF] tw-rounded-2xl"
                  : "hover:tw-bg-[#EEF2FF] hover:tw-rounded-2xl"
              } tw-w-full tw-text-left`}
            >
              <div className="tw-flex tw-justify-between tw-items-center">
                <h6
                  className={`tw-text-sm tw-font-normal ${
                    selectedDocument === doc.uploaded_document?.id
                      ? "tw-text-[#146C94]"
                      : "tw-text-[#809199]"
                  }`}
                >
                  <ElipsedText text={doc.document_name} />
                </h6>
                {getUnreadCount(doc.uploaded_document?.room_id) > 0 && (
                  <span className="tw-text-sm tw-bg-[#146C94] tw-w-7 tw-h-7 tw-max-w-7 tw-max-h-7 tw-min-w-7 tw-min-h-7 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-text-[#FFFFFF]">
                    <span className="tw-text-xs">
                      {getUnreadCount(doc.uploaded_document?.room_id)}
                    </span>
                  </span>
                )}
              </div>
            </button>
          ))}
        </div>

        {selectedDocument && documentChatRoomId && (
          <div className="tw-rounded-lg tw-border tw-border-[#E9EAEB] tw-w-full">
            {isThreadVisible && (
              <div className="tw-flex tw-items-center tw-justify-between tw-py-4 tw-px-4 tw-rounded-t-lg tw-bg-[#FFFFFF] tw-border-b tw-border-[#E9EAEB]">
                <h6 className="tw-text-[#181D27] tw-text-sm tw-font-medium">
                  Thread
                </h6>
                <button
                  onClick={() => setIsThreadVisible(false)}
                  className="tw-text-gray-400 tw-hover:text-gray-600"
                >
                  <img
                    src={closeIcon}
                    alt="Lock Icon"
                    className="tw-w-4 tw-h-4"
                  />
                </button>
              </div>
            )}
            <ChatMessages
              isDocument={true}
              isThread={isThreadVisible}
              threadMessageId={threadMessageId || undefined}
              documentId={selectedDocument}
              customRoomId={documentChatRoomId}
              onReplyClick={(message) => {
                setThreadMessageId(message.id);
                setIsThreadVisible(true);
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default LiveChatFinancialDocument;
